/**
 * Clive: drive the theme-native subscription picker on coffee PDPs.
 *
 * Replaces Recharge's subscription-widget-v2 app block that live used
 * (../clive-theme/templates/product.json:69). Recharge's Shopify Checkout Integration
 * writes real Shopify selling plans, so the only thing that has to reach the cart is a
 * `selling_plan` input inside the product form.
 *
 * How the value gets submitted: snippets/product-form.liquid renders
 * <input name="selling_plan" disabled> inside the form. theme.js builds its payload with
 * `new FormData(form)`, which omits disabled inputs — so "one-time" posts no selling_plan
 * at all, and there is no need to clear a stale value. Choosing a frequency enables the
 * input and sets it.
 *
 * Prices are never formatted here. Liquid pre-renders every variant/plan price with the
 * shop's own `money` filter into hidden nodes and this swaps the text, the same approach as
 * assets/clive-product-rewards.js. That keeps currency, locale and any market pricing
 * correct without reimplementing Shopify's formatting.
 *
 * `product:variant-change` is dispatched on `document` by theme.js only when
 * window.flu.states.useCustomEvents is true — snippets/theme-globals.liquid sets it.
 */
(function () {
  function formFor(root) {
    // The form lives in the buy_buttons block, a sibling further down the product column,
    // so scope to the section rather than walking up from the block.
    var section = root.closest('[data-section-id]') || document;
    return section.querySelector('[data-product-form]');
  }

  function planInput(root) {
    var form = formFor(root);
    return form ? form.querySelector('[data-clive-selling-plan-input]') : null;
  }

  function currentVariantId(root) {
    var form = formFor(root);
    if (!form) return null;
    var select = form.querySelector('[data-variant-select]');
    return select ? select.value : null;
  }

  function priceNode(root, variantId, planId) {
    return root.querySelector(
      '[data-clive-sub-variant="' + variantId + '"][data-clive-sub-plan="' + planId + '"]'
    );
  }

  function renderSubscriptionPrice(root, variantId) {
    var select = root.querySelector('[data-clive-sub-select]');
    var display = root.querySelector('[data-clive-sub-price-display]');
    if (!select || !display || !variantId) return;

    var node = priceNode(root, variantId, select.value);
    if (!node) {
      // This variant has no allocation for the chosen plan. Leave the last good figure
      // rather than blanking the price or inventing one.
      return;
    }

    var compare = node.getAttribute('data-clive-sub-compare');
    display.textContent = node.textContent.trim();
    if (compare) {
      var was = document.createElement('s');
      was.className = 'clive-sub__was';
      was.textContent = compare;
      display.appendChild(document.createTextNode(' '));
      display.appendChild(was);
    }
  }

  function renderOneTimePrice(root, variantId) {
    var display = root.querySelector('[data-clive-sub-onetime-price]');
    var node = root.querySelector('[data-clive-sub-onetime-for="' + variantId + '"]');
    if (display && node) display.textContent = node.textContent.trim();
  }

  function applyMode(root) {
    var subscribing = !!root.querySelector('[data-clive-sub-mode="subscribe"]:checked');
    var frequency = root.querySelector('[data-clive-sub-frequency]');
    var select = root.querySelector('[data-clive-sub-select]');
    var input = planInput(root);

    if (frequency) frequency.hidden = !subscribing;
    root.classList.toggle('clive-sub--subscribing', subscribing);

    if (!input) return;
    if (subscribing && select && select.value) {
      input.value = select.value;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  }

  function wire(root) {
    if (root.hasAttribute('data-clive-sub-wired')) return;
    root.setAttribute('data-clive-sub-wired', '');

    root.addEventListener('change', function (evt) {
      var target = evt.target;
      if (!target) return;

      if (target.hasAttribute('data-clive-sub-mode')) {
        applyMode(root);
        return;
      }

      if (target.hasAttribute('data-clive-sub-select')) {
        applyMode(root);
        renderSubscriptionPrice(root, currentVariantId(root));
      }
    });

    applyMode(root);

    // Liquid renders the FIRST allocation Shopify returns, but the <select> is sorted by
    // interval — those need not be the same plan. Today every Clive plan carries the same
    // 10% policy so the figures match either way, but that is a fact about the current
    // Recharge config, not a guarantee. Re-render against the actually-selected option so
    // the price is right even if per-frequency discounts are introduced later.
    renderSubscriptionPrice(root, currentVariantId(root));
  }

  function init() {
    var roots = document.querySelectorAll('[data-clive-sub]');
    for (var i = 0; i < roots.length; i++) wire(roots[i]);
  }

  document.addEventListener('product:variant-change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    if (!variant) return;

    // theme.js dispatches on `document` with no section scope, so every subscription block
    // on the page is offered the variant. Ownership is decided by whether a block
    // pre-rendered a price for that variant id — one belonging to another product (quick
    // view, featured product) has none and is left untouched.
    var roots = document.querySelectorAll('[data-clive-sub]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (!root.querySelector('[data-clive-sub-onetime-for="' + variant.id + '"]')) continue;
      renderOneTimePrice(root, variant.id);
      renderSubscriptionPrice(root, variant.id);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Quick view injects a product form after load.
  document.addEventListener('quickview:loaded', init);
})();
